import axios from "axios";
import Cookies from "js-cookie";
import AuthService from "./auth.service";

// État global pour gérer le verrouillage du processus de refresh
let isRefreshing = false;
let refreshSubscribers = [];
let refreshTokenFailCount = 0;
const MAX_REFRESH_ATTEMPTS = 2;

// Store pour éviter les redirections multiples
let isRedirecting = false;
let sessionExpiredModalShown = false;

// Fonction pour décoder un JWT et vérifier son expiration
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    // Décoder le JWT (partie payload)
    const base64Url = token.split(".")[1];
    if (!base64Url) return true;

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    const payload = JSON.parse(jsonPayload);
    const currentTime = Math.floor(Date.now() / 1000);

    // Vérifier si le token est expiré (avec une marge de 30 secondes)
    return payload.exp < currentTime + 30;
  } catch (error) {
    console.error("Erreur lors du décodage du token:", error);
    return true; // Considérer comme expiré en cas d'erreur
  }
};

// Fonction pour vérifier si le refresh token est expiré
const isRefreshTokenExpired = () => {
  const refreshToken = Cookies.get("refresh_token");
  return isTokenExpired(refreshToken);
};

// Fonction pour ajouter les requêtes au queue pendant le refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Fonction pour exécuter toutes les requêtes en attente une fois le token rafraîchi
const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Fonction pour rejeter toutes les requêtes en attente
const onRefreshFailure = (error) => {
  refreshSubscribers.forEach((callback) => callback(null, error));
  refreshSubscribers = [];
};

// Fonction pour nettoyer l'état et rediriger
const cleanupAndRedirect = () => {
  if (isRedirecting) return;

  isRedirecting = true;

  // Nettoyer l'état
  isRefreshing = false;
  refreshSubscribers = [];
  refreshTokenFailCount = 0;

  // Nettoyer les tokens
  AuthService.logout();

  // Rediriger vers la page de connexion
  setTimeout(() => {
    const currentPath = window.location.pathname;
    const isAuthPage = currentPath.includes("/auth/");

    if (!isAuthPage) {
      // Stocker l'URL actuelle pour la redirection après connexion
      sessionStorage.setItem("redirectAfterLogin", window.location.href);
      window.location.href = "/auth/sign-in";
    }

    isRedirecting = false;
  }, 100);
};

// Modal d'alerte pour session expirée améliorée
const showSessionExpiredAlert = () => {
  // Éviter d'afficher plusieurs modales
  if (sessionExpiredModalShown) {
    return Promise.resolve();
  }

  sessionExpiredModalShown = true;

  return new Promise((resolve) => {
    // Vérifier si on est déjà sur une page d'auth
    const currentPath = window.location.pathname;
    if (currentPath.includes("/auth/")) {
      sessionExpiredModalShown = false;
      resolve();
      return;
    }

    // Création d'un élément modal qu'on ne peut pas éviter
    const modalOverlay = document.createElement("div");
    modalOverlay.id = "session-expired-modal";
    modalOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    `;

    const modalContent = document.createElement("div");
    modalContent.style.cssText = `
      background-color: #fff;
      padding: 32px;
      border-radius: 12px;
      text-align: center;
      max-width: 420px;
      width: 90%;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      animation: modalSlideIn 0.3s ease-out;
    `;

    // Ajouter les styles d'animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes modalSlideIn {
        from {
          opacity: 0;
          transform: translateY(-20px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `;
    document.head.appendChild(style);

    // Icône d'avertissement
    const icon = document.createElement("div");
    icon.style.cssText = `
      width: 64px;
      height: 64px;
      background-color: #fef3cd;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
      font-size: 32px;
    `;
    icon.innerHTML = "⚠️";

    const title = document.createElement("h2");
    title.textContent = "Session expirée";
    title.style.cssText = `
      margin: 0 0 16px 0;
      color: #1f2937;
      font-size: 24px;
      font-weight: 600;
      line-height: 1.25;
    `;

    const message = document.createElement("p");
    message.textContent =
      "Votre session a expiré pour des raisons de sécurité. Veuillez vous reconnecter pour continuer.";
    message.style.cssText = `
      margin: 0 0 24px 0;
      color: #6b7280;
      font-size: 16px;
      line-height: 1.5;
    `;

    const countdown = document.createElement("p");
    countdown.style.cssText = `
      margin: 0 0 24px 0;
      color: #9ca3af;
      font-size: 14px;
      font-style: italic;
    `;

    const buttonContainer = document.createElement("div");
    buttonContainer.style.cssText = `
      display: flex;
      gap: 12px;
      justify-content: center;
    `;

    const reconnectButton = document.createElement("button");
    reconnectButton.textContent = "Se reconnecter";
    reconnectButton.style.cssText = `
      background-color: #57A863;
      color: #fff;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 16px;
      font-weight: 500;
      transition: background-color 0.2s;
      flex: 1;
      max-width: 150px;
    `;

    const cancelButton = document.createElement("button");
    cancelButton.textContent = "Rester ici";
    cancelButton.style.cssText = `
      background-color: #f3f4f6;
      color: #374151;
      border: 1px solid #d1d5db;
      padding: 12px 24px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 16px;
      font-weight: 500;
      transition: background-color 0.2s;
      flex: 1;
      max-width: 150px;
    `;

    // Effet hover
    reconnectButton.onmouseenter = () => {
      reconnectButton.style.backgroundColor = "#4a9154";
    };
    reconnectButton.onmouseleave = () => {
      reconnectButton.style.backgroundColor = "#57A863";
    };

    cancelButton.onmouseenter = () => {
      cancelButton.style.backgroundColor = "#e5e7eb";
    };
    cancelButton.onmouseleave = () => {
      cancelButton.style.backgroundColor = "#f3f4f6";
    };

    // Fonction de nettoyage
    let cleanup = () => {
      if (document.body.contains(modalOverlay)) {
        document.body.removeChild(modalOverlay);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      sessionExpiredModalShown = false;
    };

    // Gestionnaire de reconnexion
    const handleReconnect = () => {
      cleanup();
      cleanupAndRedirect();
      resolve();
    };

    // Gestionnaire d'annulation
    const handleCancel = () => {
      cleanup();
      resolve();
    };

    reconnectButton.onclick = handleReconnect;
    cancelButton.onclick = handleCancel;

    // Assemblage du modal
    modalContent.appendChild(icon);
    modalContent.appendChild(title);
    modalContent.appendChild(message);
    modalContent.appendChild(countdown);
    buttonContainer.appendChild(cancelButton);
    buttonContainer.appendChild(reconnectButton);
    modalContent.appendChild(buttonContainer);
    modalOverlay.appendChild(modalContent);

    // Empêcher la fermeture par clic sur l'overlay
    modalOverlay.onclick = (e) => {
      if (e.target === modalOverlay) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.body.appendChild(modalOverlay);

    // Auto-redirect avec countdown
    let secondsLeft = 30;
    const updateCountdown = () => {
      if (secondsLeft > 0) {
        countdown.textContent = `Redirection automatique dans ${secondsLeft} seconde${
          secondsLeft > 1 ? "s" : ""
        }`;
        secondsLeft--;
        setTimeout(updateCountdown, 1000);
      } else {
        handleReconnect();
      }
    };

    updateCountdown();

    // Gérer les raccourcis clavier
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        handleReconnect();
      } else if (e.key === "Escape") {
        handleCancel();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    // Nettoyer les event listeners quand le modal est fermé
    const originalCleanup = cleanup;
    cleanup = () => {
      document.removeEventListener("keydown", handleKeyPress);
      originalCleanup();
    };
  });
};

// Fonction pour gérer l'échec de session
const handleSessionFailure = async (error) => {
  console.warn("Échec de session détecté:", error.message);

  refreshTokenFailCount++;

  if (refreshTokenFailCount >= MAX_REFRESH_ATTEMPTS) {
    console.log("Nombre maximum de tentatives atteint, affichage de la modal");
    refreshTokenFailCount = 0;
    await showSessionExpiredAlert();
  } else {
    console.log("Redirection directe vers la page de connexion");
    cleanupAndRedirect();
  }
};

const axiosConfig = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosConfig.interceptors.request.use(
  async (config) => {
    const token = Cookies.get("access_token");

    if (token) {
      // Vérifier si le token est expiré avant d'envoyer la requête
      if (isTokenExpired(token)) {
        console.log("Token expiré détecté, tentative de refresh...");

        // Vérifier si le refresh token est aussi expiré
        if (isRefreshTokenExpired()) {
          console.log("Refresh token également expiré");
          await handleSessionFailure(new Error("Refresh token expiré"));
          return Promise.reject(new Error("Session expirée"));
        }

        // Si un refresh est déjà en cours, attendre
        if (isRefreshing) {
          try {
            const newToken = await new Promise((resolve, reject) => {
              subscribeTokenRefresh((token, error) => {
                if (error) {
                  reject(error);
                } else {
                  resolve(token);
                }
              });
            });

            if (newToken) {
              config.headers["Authorization"] = `Bearer ${newToken}`;
            }
          } catch (error) {
            return Promise.reject(error);
          }
        } else {
          // Démarrer le processus de refresh
          isRefreshing = true;

          try {
            await AuthService.refreshToken();
            const newToken = Cookies.get("access_token");

            if (!newToken || isTokenExpired(newToken)) {
              throw new Error("Token refresh failed");
            }

            // Réinitialiser le compteur d'échecs après un refresh réussi
            refreshTokenFailCount = 0;

            // Appliquer le nouveau token à toutes les requêtes en attente
            onTokenRefreshed(newToken);
            isRefreshing = false;

            config.headers["Authorization"] = `Bearer ${newToken}`;
          } catch (refreshError) {
            isRefreshing = false;
            onRefreshFailure(refreshError);
            await handleSessionFailure(refreshError);
            return Promise.reject(refreshError);
          }
        }
      } else {
        // Token valide, l'ajouter aux headers
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosConfig.interceptors.response.use(
  (response) => {
    // Réinitialiser le compteur d'échecs sur une réponse réussie
    refreshTokenFailCount = 0;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Gestion des erreurs de réseau
    if (!error.response) {
      console.error("Erreur de réseau:", error.message);
      return Promise.reject(error);
    }

    // Si l'erreur est 401 et ce n'est pas déjà une tentative de refresh
    if (error.response.status === 401 && !originalRequest.__isRetryRequest) {
      const token = Cookies.get("access_token");

      if (isTokenExpired(token) || !token) {
        console.log("Token expiré dans la réponse 401");

        // Vérifier si le refresh token est aussi expiré
        if (isRefreshTokenExpired()) {
          await handleSessionFailure(new Error("Refresh token expiré"));
          return Promise.reject(error);
        }

        // Si un refresh est déjà en cours, ajouter cette requête à la queue
        if (isRefreshing) {
          try {
            const newToken = await new Promise((resolve, reject) => {
              subscribeTokenRefresh((token, error) => {
                if (error) {
                  reject(error);
                } else {
                  resolve(token);
                }
              });
            });

            if (newToken) {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return axiosConfig(originalRequest);
            }
          } catch (err) {
            return Promise.reject(err);
          }
        }

        // Marquer qu'un process de refresh est en cours
        isRefreshing = true;
        originalRequest.__isRetryRequest = true;

        try {
          await AuthService.refreshToken();
          const newToken = Cookies.get("access_token");

          if (!newToken || isTokenExpired(newToken)) {
            throw new Error("Token refresh failed");
          }

          // Réinitialiser le compteur d'échecs après un refresh réussi
          refreshTokenFailCount = 0;

          // Appliquer le nouveau token à toutes les requêtes en attente
          onTokenRefreshed(newToken);

          // Réessayer la requête originale avec le nouveau token
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          isRefreshing = false;
          return axiosConfig(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshFailure(refreshError);
          await handleSessionFailure(refreshError);
          return Promise.reject(refreshError);
        }
      } else {
        // Le token n'est pas expiré, donc c'est probablement un problème d'autorisation
        console.log(
          "Erreur 401 due à un manque d'autorisation, pas à une expiration de token"
        );
      }
    }

    // Gérer d'autres codes d'erreur spécifiques
    if (error.response.status === 403) {
      console.warn("Accès interdit (403) - Permissions insuffisantes");
    } else if (error.response.status >= 500) {
      console.error("Erreur serveur:", error.response.status);
    }

    return Promise.reject(error);
  }
);

// Fonction pour vérifier périodiquement l'état des tokens
const startTokenMonitoring = () => {
  setInterval(() => {
    const token = Cookies.get("access_token");
    if (token && isTokenExpired(token) && !isRefreshing) {
      console.log("Token expiré détecté lors de la surveillance périodique");
      // Déclencher un refresh préventif si nécessaire
    }
  }, 60000); // Vérifier toutes les minutes
};

// Démarrer la surveillance des tokens
startTokenMonitoring();

// Écouter les changements de visibilité de la page pour vérifier les tokens
document.addEventListener("visibilitychange", () => {
  if (!document.hidden) {
    const token = Cookies.get("access_token");
    if (token && isTokenExpired(token)) {
      console.log("Token expiré détecté lors du retour sur la page");
    }
  }
});

export default axiosConfig;
